/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Play, Square, MapPin, Calendar, User, Phone, Clock,
  Plus, Trash2, Upload, Package, Wrench, Camera, CheckCircle, X, AlertTriangle, Film
} from 'lucide-react';
import TecnicoLayout from '../components/TecnicoLayout';
import AnnullaTimerModal from '../components/AnnullaTimerModal';
import { interventiAPI } from '../api/interventi';
import { sessioniAPI } from '../api/sessioni';
import { materialiAPI } from '../api/materiali';
import { strumentiAPI } from '../api/strumenti';
import { filesAPI } from '../api/files';
import { useElapsed, fmtElapsed } from '../hooks/useTimer';
import { useFineSessione } from '../hooks/useFineSessione';
import { fmtDurata, secondiSessione, secondiTotaliSessioni } from '../utils/durata';
import { useViewport } from '../hooks/useViewport';
import { fmtDataOra, fmtOra } from '../utils/date';

const ACCENT = '#10B981';
const UPLOADS_URL = process.env.REACT_APP_UPLOADS_URL || '';
const STATO_LABELS = { in_attesa: 'In attesa', confermato: 'Confermato', in_corso: 'In corso', completato: 'Completato', annullato: 'Annullato' };
const CATEGORIE_INTERNE = { magazzino: 'Magazzino', manutenzione_automezzi: 'Manutenzione automezzi', formazione: 'Formazione', riunione: 'Riunione', altro: 'Altro' };
const DICHIARAZIONE_LEGALE = "Consapevole delle sanzioni penali e della decadenza dai benefici nel caso di dichiarazioni non veritiere, di formazione o uso di atti falsi, richiamate dall'art. 76 del D.P.R. n. 445/2000, dichiaro che le informazioni fornite sono corrette e mi assumo la responsabilità della loro veridicità.";

export default function InterventoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileInput = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [stopModal, setStopModal] = useState(false);
  const [annullaModal, setAnnullaModal] = useState(false);
  const [completaModal, setCompletaModal] = useState(false);
  const [matModal, setMatModal] = useState(false);
  const [strModal, setStrModal] = useState(false);
  const [msg, setMsg] = useState('');
  const [okMsg, setOkMsg] = useState('');

  const { data: i, isLoading } = useQuery({ queryKey: ['intervento-tec', id], queryFn: () => interventiAPI.detail(id) });
  const { data: sessioneAttiva } = useQuery({ queryKey: ['sessione-attiva'], queryFn: () => sessioniAPI.attiva() });
  const { data: files = [] } = useQuery({ queryKey: ['files-tec', id], queryFn: () => filesAPI.listByIntervento(id) });
  const { data: strumenti = [] } = useQuery({ queryKey: ['strumenti-int', id], queryFn: () => interventiAPI.listStrumenti(id) });

  const sessioneQui = sessioneAttiva && sessioneAttiva.intervento_id === id ? sessioneAttiva : null;
  const elapsed = useElapsed(sessioneQui?.inizio);

  const startMut = useMutation({
    mutationFn: () => sessioniAPI.start(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['sessione-attiva'] }); qc.invalidateQueries({ queryKey: ['intervento-tec', id] }); },
    onError: (e) => setMsg(e.response?.data?.error || 'Errore avvio timer'),
  });

  const uploadMut = useMutation({
    mutationFn: (file) => filesAPI.upload(id, file, 'foto', setUploadProgress),
    onSuccess: () => { setUploadProgress(null); qc.invalidateQueries({ queryKey: ['files-tec', id] }); },
    onError: (e) => { setUploadProgress(null); setMsg(e.response?.data?.error || 'Errore upload'); },
  });

  const handleFiles = (e) => {
    setMsg('');
    Array.from(e.target.files).forEach((f) => {
      if (f.size > 50 * 1024 * 1024) { setMsg(`"${f.name}" supera 50MB`); return; }
      uploadMut.mutate(f);
    });
    e.target.value = '';
  };

  if (isLoading || !i) return <TecnicoLayout><div style={styles.loading}>Caricamento...</div></TecnicoLayout>;

  const completato = i.stato === 'completato';
  const fotoCount = files.filter(f => f.tipo && f.tipo.startsWith('foto')).length;
  const secondiTotali = secondiTotaliSessioni(i.sessioni);

  return (
    <TecnicoLayout>
      <button onClick={() => navigate(-1)} style={styles.backBtn}><ArrowLeft size={16} /> Indietro</button>

      <div style={styles.headerCard}>
        <div style={styles.badges}>
          <span style={{ ...styles.statoBadge, backgroundColor: i.stato_colore + '22', color: i.stato_colore }}>{STATO_LABELS[i.stato]}</span>
          <code style={styles.code}>{i.codice}</code>
        </div>
        <h1 style={styles.title}>{i.titolo}</h1>
        {i.descrizione && <p style={styles.desc}>{i.descrizione}</p>}
        <div style={styles.metaGrid}>
          {i.cliente_id ? (
            <div><User size={14} /> {i.cliente_nome} {i.cliente_cognome}</div>
          ) : (
            <div style={styles.internoBadge}>Lavoro interno{i.categoria_interna ? ` · ${CATEGORIE_INTERNE[i.categoria_interna] || i.categoria_interna}` : ''}</div>
          )}
          {i.cliente_telefono && <div><Phone size={14} /> <a href={`tel:${i.cliente_telefono}`} style={{ color: ACCENT }}>{i.cliente_telefono}</a></div>}
          {(i.indirizzo || i.citta) && <div><MapPin size={14} /> {[i.indirizzo, i.citta, i.cap].filter(Boolean).join(', ')}</div>}
          {i.data_appuntamento && <div><Calendar size={14} /> {fmtDataOra(i.data_appuntamento)}</div>}
        </div>
      </div>

      {msg && <div style={styles.msgBox}>{msg} <button onClick={() => setMsg('')} style={styles.msgClose}><X size={14} /></button></div>}
      {okMsg && <div style={styles.okBox}>{okMsg} <button onClick={() => setOkMsg('')} style={styles.msgClose}><X size={14} /></button></div>}

      {!completato && (
        <div style={styles.timerSection}>
          {sessioneQui ? (
            <>
              <div style={styles.timerLive}>
                <div style={styles.timerLabel}>⏱️ In corso</div>
                <div style={styles.timerBig}>{fmtElapsed(elapsed)}</div>
                {elapsed / 3600 >= 8 && (
                  <div style={styles.warn8h}><AlertTriangle size={14} /> Sessione oltre 8h, verifica gli orari allo stop</div>
                )}
              </div>
              <button onClick={() => setStopModal(true)} style={styles.stopBtn}><Square size={18} /> Ferma timer</button>
              <button onClick={() => setAnnullaModal(true)} style={styles.annullaBtn}>
                <Trash2 size={15} /> Avviato per errore? Annulla senza conteggiare
              </button>
            </>
          ) : sessioneAttiva ? (
            <div style={styles.timerBusy}>
              <AlertTriangle size={16} color="#92400E" />
              <span>Hai un timer attivo su <strong>{sessioneAttiva.codice}</strong>. Fermalo prima di iniziarne un altro.</span>
            </div>
          ) : (
            <button onClick={() => startMut.mutate()} disabled={startMut.isPending} style={styles.startBtn}>
              <Play size={20} /> {startMut.isPending ? 'Avvio...' : 'Inizia lavoro'}
            </button>
          )}
        </div>
      )}

      {/* MATERIALI */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h3 style={styles.sectionTitle}><Package size={18} /> Materiali usati</h3>
          {!completato && <button onClick={() => setMatModal(true)} style={styles.addBtn}><Plus size={15} /> Aggiungi</button>}
        </div>
        {!i.materiali || i.materiali.length === 0 ? (
          <div style={styles.empty}>Nessun materiale registrato.</div>
        ) : (
          <div style={styles.list}>
            {i.materiali.map((m) => (
              <div key={m.id} style={styles.rowItem}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{m.materiale_nome || m.nome}</div>
                  <div style={styles.rowMeta}>{parseFloat(m.quantita_usata)} {m.unita_misura || 'pz'} · €{parseFloat(m.prezzo_unitario || 0).toFixed(2)}/cad</div>
                </div>
                {!completato && (
                  <button onClick={() => { if (window.confirm('Rimuovere? La scorta verrà ripristinata.')) removeMat(m.id); }} style={styles.delBtn}><Trash2 size={14} /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* STRUMENTI */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h3 style={styles.sectionTitle}><Wrench size={18} /> Strumenti usati</h3>
          {!completato && <button onClick={() => setStrModal(true)} style={styles.addBtn}><Plus size={15} /> Aggiungi</button>}
        </div>
        {strumenti.length === 0 ? (
          <div style={styles.empty}>Nessuno strumento registrato.</div>
        ) : (
          <div style={styles.list}>
            {strumenti.map((s) => (
              <div key={s.id} style={styles.rowItem}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{s.strumento_nome}</div>
                  <div style={styles.rowMeta}>{s.numero_seriale || `qta ${s.quantita}`}{s.costo_noleggio ? ` · noleggio €${parseFloat(s.costo_noleggio).toFixed(2)}` : ''}</div>
                </div>
                {!completato && (
                  <button onClick={() => { if (window.confirm('Rimuovere strumento?')) removeStr(s.id); }} style={styles.delBtn}><Trash2 size={14} /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* SESSIONI / ORE */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h3 style={styles.sectionTitle}><Clock size={18} /> Ore lavorate</h3>
          <span style={styles.oreTotali}>{fmtDurata(secondiTotali)} totali</span>
        </div>
        {!i.sessioni || i.sessioni.filter(s => s.fine).length === 0 ? (
          <div style={styles.empty}>Nessuna sessione registrata.</div>
        ) : (
          <div style={styles.list}>
            {i.sessioni.filter(s => s.fine).map((s) => (
              <div key={s.id} style={styles.rowItem}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{fmtDataOra(s.inizio)} → {fmtOra(s.fine)}</div>
                  <div style={styles.rowMeta}>
                    {s.tecnico_nome} {s.tecnico_cognome}
                    {s.pausa_minuti > 0 ? ` · pausa ${s.pausa_minuti}min` : ''}
                    {s.dichiarazione ? ` · ${s.dichiarazione.substring(0, 40)}${s.dichiarazione.length > 40 ? '...' : ''}` : ''}
                  </div>
                </div>
                <div style={styles.oreBadge}>{fmtDurata(secondiSessione(s))}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* FOTO */}
      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <h3 style={styles.sectionTitle}><Camera size={18} /> Foto ({fotoCount})</h3>
        </div>
        <input ref={fileInput} type="file" accept="image/*,video/*,application/pdf" multiple capture="environment" onChange={handleFiles} style={{ display: 'none' }} />
        {!completato && (
          <button onClick={() => fileInput.current && fileInput.current.click()} disabled={uploadProgress !== null} style={styles.uploadBtn}>
            <Upload size={16} /> {uploadProgress !== null ? `${uploadProgress}%` : 'Scatta / carica foto'}
          </button>
        )}
        {uploadProgress !== null && <div style={styles.progBar}><div style={{ ...styles.progFill, width: `${uploadProgress}%` }} /></div>}
        {files.length > 0 && (
          <div style={styles.filesGrid}>
            {files.map((f) => <FileThumb key={f.id} f={f} onDelete={completato ? null : () => { if (window.confirm('Eliminare?')) delFile(f.id); }} />)}
          </div>
        )}
      </div>

      {!completato && (
        <button onClick={() => setCompletaModal(true)} style={styles.completaBtn}>
          <CheckCircle size={20} /> Completa intervento
        </button>
      )}
      {completato && (
        <div style={styles.completatoBox}><CheckCircle size={20} color="#065F46" /> Intervento completato</div>
      )}

      {stopModal && <StopModal sessione={sessioneQui} elapsed={elapsed} onClose={() => setStopModal(false)} onDone={() => { setStopModal(false); qc.invalidateQueries({ queryKey: ['sessione-attiva'] }); qc.invalidateQueries({ queryKey: ['intervento-tec', id] }); }} />}
      {annullaModal && sessioneQui && (
        <AnnullaTimerModal
          sessione={sessioneQui}
          onClose={() => setAnnullaModal(false)}
          onDone={() => {
            setAnnullaModal(false);
            setOkMsg('Timer annullato: quelle ore non sono state registrate.');
            qc.invalidateQueries({ queryKey: ['sessione-attiva'] });
            qc.invalidateQueries({ queryKey: ['intervento-tec', id] });
          }}
        />
      )}
      {matModal && <MaterialeModal interventoId={id} onClose={() => setMatModal(false)} onDone={() => { setMatModal(false); qc.invalidateQueries({ queryKey: ['intervento-tec', id] }); }} setMsg={setMsg} />}
      {strModal && <StrumentoModal interventoId={id} onClose={() => setStrModal(false)} onDone={() => { setStrModal(false); qc.invalidateQueries({ queryKey: ['strumenti-int', id] }); }} setMsg={setMsg} />}
      {completaModal && <CompletaModal interventoId={id} fotoCount={fotoCount} onClose={() => setCompletaModal(false)} onDone={() => { setCompletaModal(false); qc.invalidateQueries({ queryKey: ['intervento-tec', id] }); navigate('/interventi'); }} setMsg={setMsg} />}
    </TecnicoLayout>
  );

  function removeMat(rigaId) { interventiAPI.removeMateriale(id, rigaId).then(() => qc.invalidateQueries({ queryKey: ['intervento-tec', id] })).catch(e => setMsg(e.response?.data?.error || 'Errore')); }
  function removeStr(rigaId) { interventiAPI.removeStrumento(id, rigaId).then(() => qc.invalidateQueries({ queryKey: ['strumenti-int', id] })).catch(() => setMsg('Errore')); }
  function delFile(fid) { filesAPI.remove(fid).then(() => qc.invalidateQueries({ queryKey: ['files-tec', id] })).catch(() => setMsg('Errore eliminazione')); }
}

function FileThumb({ f, onDelete }) {
  const isImg = f.mime_type && f.mime_type.startsWith('image/');
  const isVid = f.mime_type && f.mime_type.startsWith('video/');
  const url = `${UPLOADS_URL}/${f.path ? f.path.replace('/uploads/', '') : f.nome_file}`;
  const proc = f.stato_processamento === 'pending' || f.stato_processamento === 'processing';
  return (
    <div style={styles.thumb}>
      {proc ? <div style={styles.thumbProc}><Clock size={24} color="#9CA3AF" /><span style={{ fontSize: '0.7rem' }}>Elaboro...</span></div>
        : isImg ? <a href={url} target="_blank" rel="noreferrer"><img src={url} alt="" style={styles.thumbImg} /></a>
        : isVid ? <a href={url} target="_blank" rel="noreferrer" style={styles.thumbIcon}><Film size={24} color="#6B7280" /></a>
        : <a href={url} target="_blank" rel="noreferrer" style={styles.thumbIcon}><Camera size={24} color="#6B7280" /></a>}
      {onDelete && <button onClick={onDelete} style={styles.thumbDel}><X size={12} /></button>}
    </div>
  );
}

function StopModal({ sessione, elapsed, onClose, onDone }) {
  const { isMobile } = useViewport();
  const f = useFineSessione(sessione.inizio);
  const [dichiarazione, setDichiarazione] = useState('');
  const [note, setNote] = useState('');
  const [accettato, setAccettato] = useState(false);
  const [err, setErr] = useState('');

  const dichOk = dichiarazione.trim().length > 0;

  const stopMut = useMutation({
    mutationFn: () => sessioniAPI.stop(sessione.id, {
      note: note || null,
      pausa_minuti: f.minutiPausa,
      fine: f.fine.toISOString(),
      dichiarazione,
      dichiarazione_accettata: accettato,
    }),
    onSuccess: onDone,
    onError: (e) => setErr(e.response?.data?.error || 'Errore'),
  });

  const puoConfermare = dichOk && accettato && f.puoSalvare && !stopMut.isPending;

  return (
    <div style={{ ...styles.overlay, ...(isMobile ? styles.overlayMobile : {}) }} onClick={onClose}>
      <div style={{ ...styles.modal, ...(isMobile ? styles.modalMobile : {}) }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Conferma fine lavoro</h3>
        {f.oltreUnGiorno && (
          <div style={styles.avvisoLungo}>
            <AlertTriangle size={16} />
            <span>
              Questo timer gira da <strong>{fmtDurata(f.durataTimer)}</strong>. Controlla data e ora di fine:
              verranno conteggiate solo le ore che indichi qui. Se era partito per errore, chiudi questa finestra e usa "Annulla".
            </span>
          </div>
        )}
        <div style={styles.field}>
          <label style={styles.lbl}>Inizio</label>
          <div style={styles.readonly}>{fmtDataOra(sessione.inizio)}</div>
        </div>
        {f.mostraData && (
          <div style={styles.field}>
            <label style={styles.lbl}>Data fine</label>
            <input type="date" value={f.fineData} onChange={(e) => f.setFineData(e.target.value)} style={styles.input} />
          </div>
        )}
        <div style={styles.field}>
          <label style={styles.lbl}>Ora fine (modificabile)</label>
          <input type="time" value={f.fineOra} onChange={(e) => f.setFineOra(e.target.value)} style={styles.input} />
        </div>
        <div style={styles.field}>
          <label style={styles.lbl}>Pausa (minuti)</label>
          <input type="number" min="0" value={f.pausa} onChange={(e) => f.setPausa(e.target.value)} style={styles.input} />
        </div>
        <div style={styles.field}>
          <label style={styles.lbl}>Dichiara lavoro svolto *</label>
          <textarea value={dichiarazione} onChange={(e) => setDichiarazione(e.target.value)} style={{ ...styles.input, minHeight: '60px', fontFamily: 'inherit' }} placeholder="Cosa hai fatto in questa sessione..." />
        </div>
        <div style={styles.field}>
          <label style={styles.lbl}>Note (opzionale)</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} style={{ ...styles.input, minHeight: '45px', fontFamily: 'inherit' }} />
        </div>
        <div style={styles.oreStimate}>Ore conteggiate: <strong>{fmtDurata(f.secondiStimati)}</strong></div>
        <label style={styles.checkboxRow}>
          <input type="checkbox" checked={accettato} onChange={(e) => setAccettato(e.target.checked)} style={{ width: '18px', height: '18px', flexShrink: 0, marginTop: '2px' }} />
          <span style={styles.checkboxText}>{DICHIARAZIONE_LEGALE}</span>
        </label>
        {f.primaDellInizio && <div style={styles.errBox}>⚠️ La fine deve essere successiva all'inizio. Controlla la data.</div>}
        {f.nelFuturo && <div style={styles.errBox}>⚠️ La fine è nel futuro. Correggila per continuare.</div>}
        {err && <div style={styles.errBox}>{err}</div>}
        <div style={{ ...styles.modalActions, ...(isMobile ? styles.modalActionsMobile : {}) }}>
          <button onClick={onClose} style={{ ...styles.cancelBtn, ...(isMobile ? styles.modalBtnMobile : {}) }}>Annulla</button>
          <button onClick={() => stopMut.mutate()} disabled={!puoConfermare} style={{ ...styles.confirmBtn, ...(isMobile ? styles.modalBtnMobile : {}), opacity: puoConfermare ? 1 : 0.5 }}>
            {stopMut.isPending ? 'Salvo...' : 'Conferma stop'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MaterialeModal({ interventoId, onClose, onDone, setMsg }) {
  const { isMobile } = useViewport();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);
  const [qta, setQta] = useState(1);
  const [err, setErr] = useState('');
  const { data: materiali = [] } = useQuery({ queryKey: ['materiali-search', q], queryFn: () => materialiAPI.list({ q: q || undefined }) });

  const addMut = useMutation({
    mutationFn: () => interventiAPI.addMateriale(interventoId, { materiale_id: sel.id, quantita_usata: parseFloat(qta) }),
    onSuccess: onDone,
    onError: (e) => setErr(e.response?.data?.error || 'Errore'),
  });

  return (
    <div style={{ ...styles.overlay, ...(isMobile ? styles.overlayMobile : {}) }} onClick={onClose}>
      <div style={{ ...styles.modal, ...(isMobile ? styles.modalMobile : {}) }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Aggiungi materiale</h3>
        {!sel ? (
          <>
            <input placeholder="Cerca materiale..." value={q} onChange={(e) => setQ(e.target.value)} style={styles.input} autoFocus />
            <div style={styles.searchList}>
              {materiali.map((m) => (
                <div key={m.id} style={styles.searchItem} onClick={() => setSel(m)}>
                  <div style={{ fontWeight: 600 }}>{m.nome}</div>
                  <div style={styles.rowMeta}>Magazzino: {parseFloat(m.quantita_magazzino)} {m.unita_misura} · €{parseFloat(m.prezzo_unitario || 0).toFixed(2)}</div>
                </div>
              ))}
              {materiali.length === 0 && <div style={styles.empty}>Nessun materiale.</div>}
            </div>
          </>
        ) : (
          <>
            <div style={styles.selBox}>
              <div style={{ fontWeight: 600 }}>{sel.nome}</div>
              <div style={styles.rowMeta}>Disponibili: {parseFloat(sel.quantita_magazzino)} {sel.unita_misura}</div>
            </div>
            <div style={styles.field}>
              <label style={styles.lbl}>Quantità usata</label>
              <input type="number" step="0.01" min="0.01" value={qta} onChange={(e) => setQta(e.target.value)} style={styles.input} autoFocus />
            </div>
            {err && <div style={styles.errBox}>{err}</div>}
            <div style={{ ...styles.modalActions, ...(isMobile ? styles.modalActionsMobile : {}) }}>
              <button onClick={() => setSel(null)} style={{ ...styles.cancelBtn, ...(isMobile ? styles.modalBtnMobile : {}) }}>Indietro</button>
              <button onClick={() => addMut.mutate()} disabled={addMut.isPending} style={{ ...styles.confirmBtn, ...(isMobile ? styles.modalBtnMobile : {}) }}>{addMut.isPending ? '...' : 'Aggiungi'}</button>
            </div>
          </>
        )}
        {!sel && <button onClick={onClose} style={{ ...styles.cancelBtn, width: '100%', marginTop: '0.75rem' }}>Chiudi</button>}
      </div>
    </div>
  );
}

function StrumentoModal({ interventoId, onClose, onDone, setMsg }) {
  const { isMobile } = useViewport();
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState('');
  const { data: strumenti = [] } = useQuery({ queryKey: ['strumenti-all'], queryFn: () => strumentiAPI.list() });

  const addMut = useMutation({
    mutationFn: () => interventiAPI.addStrumento(interventoId, { strumento_tipo_id: sel.id, note: note || null }),
    onSuccess: onDone,
    onError: (e) => setMsg(e.response?.data?.error || 'Errore'),
  });

  return (
    <div style={{ ...styles.overlay, ...(isMobile ? styles.overlayMobile : {}) }} onClick={onClose}>
      <div style={{ ...styles.modal, ...(isMobile ? styles.modalMobile : {}) }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Aggiungi strumento</h3>
        {!sel ? (
          <div style={styles.searchList}>
            {strumenti.map((s) => (
              <div key={s.id} style={styles.searchItem} onClick={() => setSel(s)}>
                <div style={{ fontWeight: 600 }}>{s.nome}</div>
                {s.categoria && <div style={styles.rowMeta}>{s.categoria}</div>}
              </div>
            ))}
            {strumenti.length === 0 && <div style={styles.empty}>Nessuno strumento.</div>}
          </div>
        ) : (
          <>
            <div style={styles.selBox}><div style={{ fontWeight: 600 }}>{sel.nome}</div></div>
            <div style={styles.field}>
              <label style={styles.lbl}>Note (opzionale)</label>
              <input value={note} onChange={(e) => setNote(e.target.value)} style={styles.input} />
            </div>
            <div style={{ ...styles.modalActions, ...(isMobile ? styles.modalActionsMobile : {}) }}>
              <button onClick={() => setSel(null)} style={{ ...styles.cancelBtn, ...(isMobile ? styles.modalBtnMobile : {}) }}>Indietro</button>
              <button onClick={() => addMut.mutate()} disabled={addMut.isPending} style={{ ...styles.confirmBtn, ...(isMobile ? styles.modalBtnMobile : {}) }}>Aggiungi</button>
            </div>
          </>
        )}
        {!sel && <button onClick={onClose} style={{ ...styles.cancelBtn, width: '100%', marginTop: '0.75rem' }}>Chiudi</button>}
      </div>
    </div>
  );
}

function CompletaModal({ interventoId, fotoCount, onClose, onDone, setMsg }) {
  const { isMobile } = useViewport();
  const [note, setNote] = useState('');
  const [accettato, setAccettato] = useState(false);
  const [err, setErr] = useState('');

  const completaMut = useMutation({
    mutationFn: () => interventiAPI.completa(interventoId, note, accettato),
    onSuccess: onDone,
    onError: (e) => setErr(e.response?.data?.error || 'Errore'),
  });

  const fotoOk = fotoCount > 0;
  const noteOk = note.trim().length > 0;
  const puoCompletare = fotoOk && noteOk && accettato && !completaMut.isPending;

  return (
    <div style={{ ...styles.overlay, ...(isMobile ? styles.overlayMobile : {}) }} onClick={onClose}>
      <div style={{ ...styles.modal, ...(isMobile ? styles.modalMobile : {}) }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>Completa intervento</h3>
        <div style={styles.checklist}>
          <div style={{ ...styles.checkItem, color: fotoOk ? '#065F46' : '#991B1B' }}>
            {fotoOk ? <CheckCircle size={16} /> : <X size={16} />} Almeno 1 foto ({fotoCount})
          </div>
          <div style={{ ...styles.checkItem, color: noteOk ? '#065F46' : '#991B1B' }}>
            {noteOk ? <CheckCircle size={16} /> : <X size={16} />} Dichiarazione lavoro svolto
          </div>
          <div style={{ ...styles.checkItem, color: accettato ? '#065F46' : '#991B1B' }}>
            {accettato ? <CheckCircle size={16} /> : <X size={16} />} Dichiarazione di responsabilità
          </div>
        </div>
        <div style={styles.field}>
          <label style={styles.lbl}>Dichiara lavoro svolto *</label>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} style={{ ...styles.input, minHeight: '90px', fontFamily: 'inherit' }} placeholder="Resoconto complessivo del lavoro svolto..." autoFocus />
        </div>
        {!fotoOk && <div style={styles.warnBox}>⚠️ Carica almeno una foto prima di completare.</div>}
        <label style={styles.checkboxRow}>
          <input type="checkbox" checked={accettato} onChange={(e) => setAccettato(e.target.checked)} style={{ width: '18px', height: '18px', flexShrink: 0, marginTop: '2px' }} />
          <span style={styles.checkboxText}>{DICHIARAZIONE_LEGALE}</span>
        </label>
        {err && <div style={styles.errBox}>{err}</div>}
        <div style={{ ...styles.modalActions, ...(isMobile ? styles.modalActionsMobile : {}) }}>
          <button onClick={onClose} style={{ ...styles.cancelBtn, ...(isMobile ? styles.modalBtnMobile : {}) }}>Annulla</button>
          <button onClick={() => completaMut.mutate()} disabled={!puoCompletare} style={{ ...styles.confirmBtn, ...(isMobile ? styles.modalBtnMobile : {}), opacity: puoCompletare ? 1 : 0.5 }}>
            {completaMut.isPending ? 'Completo...' : 'Completa'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  loading: { padding: '3rem', textAlign: 'center', color: '#6B7280' },
  backBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.9rem', backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', marginBottom: '1rem', fontSize: '0.88rem' },
  headerCard: { backgroundColor: 'white', padding: '1.25rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: '1rem' },
  badges: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' },
  statoBadge: { padding: '0.22rem 0.7rem', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 700 },
  code: { backgroundColor: '#E5E7EB', padding: '0.15rem 0.45rem', borderRadius: '4px', fontSize: '0.8rem', fontFamily: 'ui-monospace, monospace' },
  title: { margin: '0 0 0.4rem', fontSize: '1.3rem', fontWeight: 700 },
  desc: { color: '#4B5563', margin: '0 0 0.85rem', fontSize: '0.92rem' },
  metaGrid: { display: 'flex', flexDirection: 'column', gap: '0.45rem', fontSize: '0.88rem', color: '#4B5563' },
  internoBadge: { display: 'inline-block', backgroundColor: '#EDE9FE', color: '#5B21B6', padding: '0.15rem 0.55rem', borderRadius: '6px', fontWeight: 600, fontSize: '0.82rem', width: 'fit-content' },
  msgBox: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.75rem 1rem', borderRadius: '8px', fontSize: '0.88rem', marginBottom: '1rem' },
  okBox: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#D1FAE5', color: '#065F46', padding: '0.75rem 1rem', borderRadius: '8px', fontSize: '0.88rem', marginBottom: '1rem' },
  msgClose: { background: 'none', border: 'none', cursor: 'pointer', color: '#991B1B', display: 'inline-flex' },
  timerSection: { marginBottom: '1.25rem' },
  startBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', width: '100%', padding: '1.1rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '14px', cursor: 'pointer', fontWeight: 700, fontSize: '1.1rem' },
  timerLive: { background: 'linear-gradient(135deg, #10B981, #059669)', borderRadius: '14px', padding: '1.5rem', color: 'white', textAlign: 'center', marginBottom: '0.75rem' },
  timerLabel: { fontSize: '0.85rem', opacity: 0.9, fontWeight: 600 },
  timerBig: { fontSize: '3rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', margin: '0.3rem 0' },
  avvisoLungo: { display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.8rem', backgroundColor: '#FEF3C7', color: '#92400E', borderRadius: '10px', fontSize: '0.85rem', lineHeight: 1.45, marginBottom: '0.9rem' },
  warn8h: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', backgroundColor: 'rgba(255,255,255,0.2)', padding: '0.3rem 0.7rem', borderRadius: '6px', marginTop: '0.5rem' },
  stopBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', width: '100%', padding: '0.9rem', backgroundColor: '#DC2626', color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 700, fontSize: '1rem' },
  annullaBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', width: '100%', padding: '0.6rem', marginTop: '0.5rem', backgroundColor: 'transparent', color: '#6B7280', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: '0.83rem', textDecoration: 'underline' },
  timerBusy: { display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '1rem', backgroundColor: '#FEF3C7', color: '#92400E', borderRadius: '12px', fontSize: '0.88rem' },
  section: { backgroundColor: 'white', padding: '1.1rem 1.25rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: '1rem' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem' },
  sectionTitle: { margin: 0, fontSize: '1rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '0.4rem' },
  addBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.4rem 0.8rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' },
  list: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  rowItem: { display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.7rem 0.85rem', backgroundColor: '#F9FAFB', borderRadius: '8px' },
  rowMeta: { fontSize: '0.8rem', color: '#6B7280', marginTop: '0.15rem' },
  oreTotali: { fontSize: '0.9rem', fontWeight: 700, color: ACCENT },
  oreBadge: { padding: '0.3rem 0.6rem', backgroundColor: ACCENT + '20', color: ACCENT, borderRadius: '6px', fontWeight: 700, fontSize: '0.85rem', flexShrink: 0 },
  delBtn: { padding: '0.4rem', backgroundColor: 'transparent', border: 'none', color: '#DC2626', cursor: 'pointer', display: 'inline-flex' },
  empty: { padding: '1.25rem', textAlign: 'center', color: '#9CA3AF', fontSize: '0.85rem' },
  uploadBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.7rem 1.1rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 600, marginBottom: '0.85rem' },
  progBar: { height: '6px', backgroundColor: '#F3F4F6', borderRadius: '3px', overflow: 'hidden', marginBottom: '0.85rem' },
  progFill: { height: '100%', backgroundColor: ACCENT, transition: 'width 0.3s' },
  filesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '0.6rem' },
  thumb: { position: 'relative', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#F3F4F6' },
  thumbImg: { width: '100%', height: '90px', objectFit: 'cover', display: 'block', cursor: 'pointer' },
  thumbIcon: { height: '90px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  thumbProc: { height: '90px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.2rem', color: '#9CA3AF' },
  thumbDel: { position: 'absolute', top: '4px', right: '4px', width: '22px', height: '22px', borderRadius: '50%', backgroundColor: 'rgba(220,38,38,0.9)', color: 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  completaBtn: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', width: '100%', padding: '1rem', backgroundColor: '#059669', color: 'white', border: 'none', borderRadius: '14px', cursor: 'pointer', fontWeight: 700, fontSize: '1.05rem', marginBottom: '1rem' },
  completatoBox: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '1rem', backgroundColor: '#D1FAE5', color: '#065F46', borderRadius: '12px', fontWeight: 700, marginBottom: '1rem' },
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' },
  overlayMobile: { padding: 0, alignItems: 'flex-end' },
  modal: { backgroundColor: 'white', borderRadius: '14px', padding: '1.5rem', width: '100%', maxWidth: '440px', maxHeight: '90vh', overflow: 'auto' },
  modalMobile: { borderRadius: '16px 16px 0 0', padding: '1.25rem', maxWidth: '100%', maxHeight: '92vh' },
  modalActionsMobile: { gap: '0.6rem' },
  modalBtnMobile: { flex: 1, padding: '0.85rem', fontSize: '0.95rem', textAlign: 'center', justifyContent: 'center' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '0.85rem' },
  lbl: { fontSize: '0.82rem', fontWeight: 600, color: '#374151' },
  input: { padding: '0.6rem 0.8rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', width: '100%' },
  readonly: { padding: '0.6rem 0.8rem', backgroundColor: '#F3F4F6', borderRadius: '8px', fontSize: '0.95rem', color: '#6B7280' },
  oreStimate: { padding: '0.7rem', backgroundColor: '#F0FDF4', borderRadius: '8px', textAlign: 'center', fontSize: '0.95rem', marginBottom: '0.85rem' },
  checkboxRow: { display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.75rem', backgroundColor: '#F9FAFB', borderRadius: '8px', marginBottom: '0.85rem', cursor: 'pointer' },
  checkboxText: { fontSize: '0.78rem', color: '#4B5563', lineHeight: 1.4 },
  searchList: { display: 'flex', flexDirection: 'column', gap: '0.4rem', maxHeight: '300px', overflow: 'auto', marginTop: '0.75rem' },
  searchItem: { padding: '0.7rem 0.85rem', backgroundColor: '#F9FAFB', borderRadius: '8px', cursor: 'pointer' },
  selBox: { padding: '0.85rem', backgroundColor: '#F0FDF4', borderRadius: '8px', marginBottom: '0.85rem' },
  checklist: { display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: '1rem' },
  checkItem: { display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.9rem', fontWeight: 600 },
  warnBox: { backgroundColor: '#FEF3C7', color: '#92400E', padding: '0.7rem', borderRadius: '8px', fontSize: '0.85rem', marginBottom: '0.85rem' },
  errBox: { backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.65rem', borderRadius: '8px', fontSize: '0.85rem', marginBottom: '0.85rem' },
  modalActions: { display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' },
  cancelBtn: { padding: '0.6rem 1.1rem', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 },
  confirmBtn: { padding: '0.6rem 1.2rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 },
};
